/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { SnapRecentSearchEntry, RoomConfig } from '../../types';
import { TripType } from '../../types';

/**
 * Build the primary text: "Kuala Lumpur to Tokyo"
 */
export const buildPrimaryText = (
  entry: SnapRecentSearchEntry,
  template: string,
): string =>
  template
    .replace('{0}', entry.from.CityName)
    .replace('{1}', entry.to.CityName);

/**
 * Build the secondary text showing flight dates (NOT hotel dates per Scenario 7).
 * e.g. "Sun, 1 Nov – Fri, 6 Nov"
 *
 * Uses the locale-aware formatter passed from the component.
 */
export const buildSecondaryText = (
  entry: SnapRecentSearchEntry,
  formatDate: (dateStr: string) => string,
): string => {
  if (!entry.departDate) return '';

  const depart = formatDate(entry.departDate);

  if (entry.tripType === TripType.ONE_WAY || !entry.returnDate) {
    return depart;
  }

  const ret = formatDate(entry.returnDate);
  return `${depart} – ${ret}`;
};

/**
 * Build the tertiary text showing guest count.
 * e.g. "2 Guests" or "3 Adults, 1 Child"
 */
export const buildGuestText = (
  rooms: RoomConfig[],
  t: (key: string, options?: object) => string,
): string => {
  const totalAdults = rooms.reduce((sum, room) => sum + room.adults, 0);
  const totalChildren = rooms.reduce(
    (sum, room) => sum + (room.children?.length ?? 0),
    0,
  );
  const totalInfants = rooms.reduce(
    (sum, room) => sum + (room.infants ?? 0),
    0,
  );
  const totalGuests = totalAdults + totalChildren + totalInfants;

  return t('recentSearches.guests', {
    count: totalGuests,
    defaultValue: '{{count}} Guests',
  });
};
